import { StudentActivationStatus, StudentCourseEnrolmentStatus } from '@duke-hq/svc-api-db/client';
import { TERMINAL_REFERRAL_STATUSES } from '@duke-hq/svc-api/lib/enrolment/constants';

export type Enrolment = {
  status: StudentCourseEnrolmentStatus;
  course: {
    accredited: boolean;
  } | null;
};

export type Student = {
  course_referral_id: string | null;
  CourseEnrolmentReferral: Array<{
    status: StudentActivationStatus;
  }>;
};

/**
 * Determines if a student can be referred to a new course based on:
 * 1. Active accredited course check - blocks if student has PENDING/IN_PROGRESS/PAUSED accredited course
 * 2. New referral process check - requires consumed/expired/deleted referral status
 * 3. Pending enrolments check - requires no PENDING/IN_PROGRESS/PAUSED enrolments
 */
export function canReferToCourse(student: Student, enrolments: Enrolment[]): boolean {
  const hasNoPendingEnrolments =
    enrolments.length === 0 ||
    enrolments.every(
      (enrolment) =>
        enrolment.status === StudentCourseEnrolmentStatus.COMPLETED ||
        enrolment.status === StudentCourseEnrolmentStatus.WITHDRAWN ||
        enrolment.status === StudentCourseEnrolmentStatus.DELETED,
    );

  // Check if student has any active accredited course enrolments
  // Active means: PENDING, IN_PROGRESS, or PAUSED
  const hasActiveAccreditedCourse = enrolments.some(
    (enrolment) =>
      (enrolment.status === StudentCourseEnrolmentStatus.PENDING ||
        enrolment.status === StudentCourseEnrolmentStatus.IN_PROGRESS ||
        enrolment.status === StudentCourseEnrolmentStatus.PAUSED) &&
      enrolment.course?.accredited === true,
  );

  // Block re-enrolment if student has an active accredited course
  if (hasActiveAccreditedCourse) {
    return false;
  }

  const isNewReferral = Boolean(student.course_referral_id);
  const hasNoActiveReferrals =
    student.CourseEnrolmentReferral.length > 0 &&
    student.CourseEnrolmentReferral.every((r) => TERMINAL_REFERRAL_STATUSES.includes(r.status));

  if (isNewReferral) {
    return hasNoActiveReferrals && hasNoPendingEnrolments;
  }

  return hasNoPendingEnrolments;
}
